// API Configuration
// This file centralizes API base URL configuration
// In development, Vite proxy handles /api routes
// In production, use VITE_API_URL environment variable

import axios from "axios";
import { clearActiveStore } from "./activeStore.js";

const getApiBaseUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  console.log("API URL from env:", apiUrl);
  if (apiUrl) {
    return apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
  }
  return null;
};

export const API_BASE_URL = getApiBaseUrl();

// Point axios at VITE_API_URL (e.g. staging-api.retail-circle.io) in BOTH dev
// and production so relative calls like axios.get("/api/...") hit the API host
// directly instead of the dev-server origin (localhost:5173/api → Vite proxy).
// Absolute URLs built via getApiUrl() are unaffected — axios ignores baseURL
// when the request URL is already absolute. Image <img src> paths from
// getFileUrl() stay relative in dev and still resolve through the Vite proxy.
// NOTE: this makes dev API calls cross-origin, so staging must allow the
// localhost dev origin via CORS. To revert to proxy-based calls, guard this
// with `if (!import.meta.env.DEV && API_BASE_URL)`.
if (API_BASE_URL) {
  axios.defaults.baseURL = API_BASE_URL;
}

// Tells the API which screen each call came from — "Last page" on Usage
// Analytics (stored on audit_events.page). Our own API only: a custom header on
// a third-party request would force a CORS preflight that host may refuse.
axios.interceptors.request.use((config) => {
  const url = String(config.url || "");
  const ownApi = !/^https?:\/\//i.test(url) || (API_BASE_URL && url.startsWith(API_BASE_URL));
  if (ownApi && typeof window !== "undefined") {
    config.headers = config.headers || {};
    config.headers["X-RC-Page"] = window.location.pathname;
  }
  return config;
});

// Defensive retry on transient connection failures (HY000 SQLSTATE 2002 /
// 502 / 503) for idempotent GET requests. Backs off and retries up to 2
// times before surfacing the error. Avoids hard crashes when MySQL
// connection pool is momentarily exhausted or a pod restarts.
const TRANSIENT_RETRY_LIMIT = 2;
const TRANSIENT_RETRY_DELAY_MS = 600;

const isTransientFailure = (error) => {
  const status = error?.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  if (status === 500) {
    const msg = error?.response?.data?.message ?? "";
    return /Connection refused|2002|HY000|Lost connection/i.test(msg);
  }
  if (!error?.response && error?.code === "ECONNABORTED") return true;
  return false;
};

// A stored active store that the user no longer has access to.
//
// The X-Active-Store header lives in localStorage, so it outlives the access
// it names: revoke someone's membership (or fix a store that was assigned to
// them by mistake) and their browser keeps sending it. ResolveActiveStore
// answers 403 to an invalid one — correctly, since silently downgrading would
// make the membership check advisory — but that 403 lands on EVERY request, so
// the app is wedged on a blank page until the user thinks to clear site data.
// Nobody does; they report the app as broken.
//
// Recovering is unambiguous, which is why it's safe to do automatically: drop
// the stale choice and reload. The backend then re-resolves the session from
// the user's default store (or store_id_m), which is where they should have
// been all along. Guarded by a sessionStorage flag so a genuinely persistent
// 403 can never become a reload loop — one attempt per tab, then the error
// surfaces normally.
const STALE_STORE_HEAL_KEY = "activeStoreHealAttempted";

export const isRevokedActiveStore = (error) =>
  error?.response?.status === 403 &&
  error?.response?.data?.code === "invalid_active_store";

export const healRevokedActiveStore = () => {
  try {
    if (sessionStorage.getItem(STALE_STORE_HEAL_KEY)) return false;
    sessionStorage.setItem(STALE_STORE_HEAL_KEY, "1");
  } catch (e) {
    // Storage unavailable (private mode, blocked): without a working guard a
    // reload could loop, so do nothing and let the 403 surface.
    return false;
  }

  // Drops both the persisted id and the header, in one place — see
  // activeStore.js. Not hand-rolled here, so the storage key can never drift.
  clearActiveStore();
  location.reload();

  return true;
};

axios.interceptors.response.use(undefined, async (error) => {
  if (isRevokedActiveStore(error) && healRevokedActiveStore()) {
    return Promise.reject(error);
  }

  const cfg = error?.config;
  const method = (cfg?.method ?? "").toLowerCase();
  if (!cfg || method !== "get") return Promise.reject(error);
  if (!isTransientFailure(error)) return Promise.reject(error);

  cfg.__retryCount = (cfg.__retryCount ?? 0) + 1;
  if (cfg.__retryCount > TRANSIENT_RETRY_LIMIT) return Promise.reject(error);

  const delay = TRANSIENT_RETRY_DELAY_MS * cfg.__retryCount;
  await new Promise((r) => setTimeout(r, delay));
  return axios(cfg);
});

// Helper to get full API URL
export const getApiUrl = (path) => {
  // Remove leading slash if present to avoid double slashes
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  if (!API_BASE_URL) {
    return cleanPath;
  }

  // Ensure base URL doesn't end with slash
  const base = API_BASE_URL.endsWith("/")
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;

  return `${base}${cleanPath}`;
};

// Helper to get file URL (for images, documents, etc.)
// In development, return a same-origin relative path so the Vite proxy
// forwards the request and the browser doesn't try a cross-origin fetch
// to staging-api (which can fail silently in <img> tags). In production
// builds the absolute URL is used so static assets resolve against
// the configured API host.
export const getFileUrl = (fileId) => {
  if (!fileId) {
    return null;
  }
  if (import.meta.env.DEV) {
    return `/api/files/${fileId}`;
  }
  return getApiUrl(`/api/files/${fileId}`);
};

// PDFs must use the /api/pdfs endpoint — /api/files runs ImageMagick on the blob
// and 500s on PDFs. Use this for statement files that aren't images.
export const getPdfUrl = (fileId) => {
  if (!fileId) {
    return null;
  }
  if (import.meta.env.DEV) {
    return `/api/pdfs/${fileId}`;
  }
  return getApiUrl(`/api/pdfs/${fileId}`);
};
